import type { DocKind } from '../core/documents';

/**
 * The cloud as the editor sees it: five operations and a taxonomy of failures.
 * Nothing in the sync engine knows about Supabase; `supabaseApi.ts` implements
 * this, and the tests implement it with an in-memory server that mirrors the
 * migration's revision rules.
 */

export type CloudErrorKind =
  /** The request did not reach the server or got no answer. Retry later, silently. */
  | 'network'
  /** The session is missing, expired or revoked. Stop; the person has to sign in. */
  | 'auth'
  /** The account is not allowed to use cloud storage. Stop. */
  | 'denied'
  /** The server refused this content (size, invalid text). Retrying cannot help. */
  | 'rejected'
  /** The server failed or is throttling. Retry with backoff. */
  | 'server';

export class CloudError extends Error {
  constructor(
    readonly kind: CloudErrorKind,
    message: string,
  ) {
    super(message);
    this.name = 'CloudError';
  }

  get retryable(): boolean {
    return this.kind === 'network' || this.kind === 'server';
  }
}

export interface RemoteMeta {
  id: string;
  name: string;
  kind: DocKind;
  revision: number;
  updated_at: string;
  deleted_at: string | null;
  workspace_id: string | null;
  sort_order: number;
  eol: string;
  has_bom: boolean;
}

export interface RemoteDoc extends RemoteMeta {
  content: string;
}

export interface PushInput {
  id: string;
  /** The revision the caller last saw; 0 creates the document. */
  baseRevision: number;
  name: string;
  kind: DocKind;
  content: string;
  eol: 'lf' | 'crlf';
  bom: boolean;
  workspace: { id: string; name: string } | null;
  sortOrder: number;
}

export type PushResult =
  | { status: 'ok'; revision: number; updated_at: string }
  | { status: 'conflict'; document: RemoteDoc }
  | { status: 'deleted'; revision: number }
  | { status: 'not_found' };

export type DeleteResult = { status: 'ok'; revision: number } | { status: 'conflict'; document: RemoteDoc } | { status: 'not_found' };

export interface CloudApi {
  /** Whether the signed-in account may use cloud storage (the private-beta gate). */
  access(): Promise<boolean>;
  /** Every document of the account, deleted ones included, without their text. */
  list(): Promise<RemoteMeta[]>;
  /** One document with its text; null when it does not exist. */
  fetch(id: string): Promise<RemoteDoc | null>;
  /** Compare-and-swap write. Never overwrites a revision the caller has not seen. */
  push(input: PushInput): Promise<PushResult>;
  /** Tombstone a document, only at the revision the caller saw. */
  remove(id: string, baseRevision: number): Promise<DeleteResult>;
}
